import { GoldenSourceHelper } from "../../../../../app/Switch_Core_React_Standards";
import { dbPayload } from "../../../../../auth/registered/SEC_00_DB_Payload";
import { s3UtilityService } from "../../../../upload/services/S3Utilities";

// Fixed destination for ingested taxonomy catalogues - see SustinaXbrlProcessorService.js.
const TAXONOMY_BUCKET = "esg-taxonomy-reference";

/**
 * 🛰️ SYNC HEADER: upserts by (TaxYear, VersionLabel) - re-syncing the same
 * taxonomy version returns the same HeaderId rather than duplicating it.
 * Targets: EsgTaxonomySyncController.sync-header (Ref.usp_UpsertESG_TaxonomyHeader)
 */
export const syncTaxonomyHeader = async ({ taxYear, versionLabel, createdBy }, config) => {
    const rawData = await dbPayload(
        "/api/v1/sustina/esg-taxonomy/sync-header",
        "POST",
        config.siteKey,
        GoldenSourceHelper.prepareForApi({ TaxYear: taxYear, VersionLabel: versionLabel, CreatedBy: createdBy || null })
    );

    const res = GoldenSourceHelper.normalize(rawData);
    if (!res.success) throw new Error(res.message || "Taxonomy header sync failed.");
    return GoldenSourceHelper.get(res.data, "HeaderId");
};

/**
 * 🛰️ SYNC ROWS: bitemporal upsert of concepts under a HeaderId.
 * Targets: EsgTaxonomySyncController.sync-rows (Ref.usp_SyncESG_TaxonomyRows)
 */
export const syncTaxonomyRows = async (headerId, rows, config) => {
    const rawData = await dbPayload(
        "/api/v1/sustina/esg-taxonomy/sync-rows",
        "POST",
        config.siteKey,
        GoldenSourceHelper.prepareForApi({ HeaderId: headerId, JsonData: JSON.stringify(rows) })
    );

    const res = GoldenSourceHelper.normalize(rawData);
    if (!res.success) throw new Error(res.message || "Taxonomy rows sync failed.");
    return true;
};

/**
 * 🛰️ GET ALL TAXONOMY HEADERS (every synced version) — feeds the "connect
 * existing taxonomy" picker.
 * Targets: EsgTaxonomySyncController.get-headers (Ref.usp_GetESG_TaxonomyHeaders)
 */
export const fetchTaxonomyHeaders = async (config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/esg-taxonomy/get-headers",
            "POST",
            config.siteKey,
            {}
        );

        const res = GoldenSourceHelper.normalize(rawData);
        return res.success ? res.data : [];
    } catch (err) {
        console.error("Taxonomy Headers Fetch Failed:", err);
        return [];
    }
};

/**
 * 🛰️ GET CONCEPTS FOR A TAXONOMY HEADER
 * Targets: EsgTaxonomySyncController.get-details-by-header (Ref.usp_GetESG_TaxonomyDetails_ByHeader)
 */
export const fetchTaxonomyDetailsByHeader = async (headerId, config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/esg-taxonomy/get-details-by-header",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi({ HeaderId: headerId })
        );

        const res = GoldenSourceHelper.normalize(rawData);
        return res.success ? res.data : [];
    } catch (err) {
        console.error("Taxonomy Details Fetch Failed:", err);
        return [];
    }
};

/**
 * 🛰️ SYNC ROLES: the taxonomy's extended linkroles (one per disclosure table).
 * Targets: EsgTaxonomySyncController.sync-roles (Ref.usp_SyncESG_TaxonomyRoles)
 */
export const syncTaxonomyRoles = async (headerId, roles, config) => {
    const rawData = await dbPayload(
        "/api/v1/sustina/esg-taxonomy/sync-roles",
        "POST",
        config.siteKey,
        GoldenSourceHelper.prepareForApi({ HeaderId: headerId, JsonData: JSON.stringify(roles) })
    );

    const res = GoldenSourceHelper.normalize(rawData);
    if (!res.success) throw new Error(res.message || "Taxonomy roles sync failed.");
    return Number(GoldenSourceHelper.get(res.data, "RoleCount")) || 0;
};

/**
 * 🛰️ SYNC ARCS: one batch of parent -> child relationships. The first batch
 * of an ingest replaces the header's existing arcs.
 * Targets: EsgTaxonomySyncController.sync-arcs (Ref.usp_SyncESG_TaxonomyArcs)
 */
export const syncTaxonomyArcs = async (headerId, arcs, replaceExisting, config) => {
    const rawData = await dbPayload(
        "/api/v1/sustina/esg-taxonomy/sync-arcs",
        "POST",
        config.siteKey,
        GoldenSourceHelper.prepareForApi({
            HeaderId: headerId,
            JsonData: JSON.stringify(arcs),
            ReplaceExisting: !!replaceExisting,
        })
    );

    const res = GoldenSourceHelper.normalize(rawData);
    if (!res.success) throw new Error(res.message || "Taxonomy arcs sync failed.");
    return {
        received: Number(GoldenSourceHelper.get(res.data, "ReceivedCount")) || 0,
        inserted: Number(GoldenSourceHelper.get(res.data, "InsertedCount")) || 0,
        unresolved: Number(GoldenSourceHelper.get(res.data, "UnresolvedCount")) || 0,
    };
};

/**
 * 🛰️ GET ROLES (disclosure tables) of a taxonomy Header, in taxonomy order.
 * Targets: EsgTaxonomySyncController.get-roles-by-header (Ref.usp_GetESG_TaxonomyRoles_ByHeader)
 */
export const fetchTaxonomyRolesByHeader = async (headerId, config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/esg-taxonomy/get-roles-by-header",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi({ HeaderId: headerId })
        );
        const res = GoldenSourceHelper.normalize(rawData);
        return res.success ? res.data : [];
    } catch (err) {
        console.error("Taxonomy Roles Fetch Failed:", err);
        return [];
    }
};

/**
 * 🛰️ GET ARCS of a Header, optionally narrowed to one role / arc kind.
 * Targets: EsgTaxonomySyncController.get-arcs-by-header (Ref.usp_GetESG_TaxonomyArcs_ByHeader)
 */
export const fetchTaxonomyArcsByHeader = async (headerId, { roleId = null, arcRoleCode = null } = {}, config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/esg-taxonomy/get-arcs-by-header",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi({ HeaderId: headerId, RoleId: roleId, ArcRoleCode: arcRoleCode })
        );
        const res = GoldenSourceHelper.normalize(rawData);
        return res.success ? res.data : [];
    } catch (err) {
        console.error("Taxonomy Arcs Fetch Failed:", err);
        return [];
    }
};

/**
 * 🛰️ GET TREE: presentation hierarchy of one role, depth-first.
 * Targets: EsgTaxonomySyncController.get-tree-by-role (Ref.usp_GetESG_TaxonomyTree_ByRole)
 */
export const fetchTaxonomyTreeByRole = async (headerId, roleId, config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/esg-taxonomy/get-tree-by-role",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi({ HeaderId: headerId, RoleId: roleId })
        );
        const res = GoldenSourceHelper.normalize(rawData);
        return res.success ? res.data : [];
    } catch (err) {
        console.error("Taxonomy Tree Fetch Failed:", err);
        return [];
    }
};

/**
 * 🎯 Header identity for an entry point, so re-ingesting the same taxonomy
 * lands on the same HeaderId. ".../esrs/2023-12-22/esrs_all.xsd" ->
 * { taxYear: 2023, versionLabel: "ESRS All (2023-12-22)" }.
 */
export const deriveTaxonomyVersion = (entryPointUrl) => {
    const url = String(entryPointUrl || "").trim();
    const date = (url.match(/(\d{4})-(\d{2})-(\d{2})/) || [])[0] || null;
    const stem = (url.split(/[?#]/)[0].split("/").pop() || "").replace(/\.(xsd|zip)$/i, "");
    const tokens = stem.split(/[_\-.]+/).filter(Boolean);
    const name = tokens
        .map((t, i) => (i === 0 ? t.toUpperCase() : t.charAt(0).toUpperCase() + t.slice(1).toLowerCase()))
        .join(" ");
    return {
        taxYear: date ? Number(date.slice(0, 4)) : new Date().getUTCFullYear(),
        versionLabel: name && date ? `${name} (${date})` : name || url,
    };
};

// Keeps each request well under the BFF Lambda's payload limit.
const BATCH_SIZE = 2000;

const chunk = (list, size) => {
    const out = [];
    for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
    return out;
};

/**
 * 🎯 Reshapes the Lambda's concept catalogue into the row shape
 * Ref.usp_SyncESG_TaxonomyRows expects. Pillar isn't derivable from the
 * taxonomy itself, so it's left null.
 */
const catalogueToRows = (catalogue) =>
    Object.values(catalogue?.concepts || {}).map((concept) => ({
        xbrlTag: concept.technicalId,
        measureName: concept.label,
        dataType: concept.dataType,
        pillar: null,
        isAbstract: concept.isAbstract ?? null,
        periodType: concept.periodType ?? null,
        balance: concept.balance ?? null,
        substitutionGroup: concept.substitutionGroup ?? null,
    }));

/**
 * 🎯 INGEST + SYNC: after the Lambda ingest writes a concept catalogue to S3
 * (SustinaXbrlProcessorService.ingestTaxonomy), fetches that JSON back via
 * the existing S3 presigned-download utility and pushes it into
 * Ref.ESG_TaxonomyHeader/ESG_TaxonomyDetails so it becomes queryable for
 * company-taxonomy binding and supplier classification.
 *
 * NOT YET VERIFIED against the live Lambda response shape for
 * s3UtilityService.getDownloadUrl - no existing caller in the codebase to
 * confirm the field name holding the presigned URL. Check this at testing
 * time (see [[sustina_dashboard_supplier_xbrl_next]]) and adjust the
 * `presignedUrl` extraction below if it doesn't match.
 */
export const syncIngestedTaxonomyToSql = async ({ bucket, key, taxYear, versionLabel, onProgress }, config) => {
    const progress = (msg) => onProgress && onProgress(msg);
    const downloadResult = await s3UtilityService.getDownloadUrl(bucket || TAXONOMY_BUCKET, config, key);
    const presignedUrl =
        GoldenSourceHelper.get(downloadResult?.data, "url") ||
        GoldenSourceHelper.get(downloadResult?.data, "downloadUrl") ||
        GoldenSourceHelper.get(downloadResult?.data, "presignedUrl");

    if (!presignedUrl) throw new Error("Could not resolve a download URL for the ingested taxonomy catalogue.");

    const response = await fetch(presignedUrl);
    if (!response.ok) throw new Error("Could not fetch the ingested taxonomy catalogue from S3.");
    const catalogue = await response.json();

    const headerId = await syncTaxonomyHeader(
        { taxYear: taxYear || new Date().getUTCFullYear(), versionLabel: versionLabel || key },
        config
    );

    // Order matters: arcs resolve against the stored concepts and roles.
    const rowBatches = chunk(catalogueToRows(catalogue), BATCH_SIZE);
    for (let i = 0; i < rowBatches.length; i++) {
        progress(`Saving concepts ${i + 1}/${rowBatches.length}...`);
        await syncTaxonomyRows(headerId, rowBatches[i], config);
    }

    const roles = catalogue?.roles || [];
    const arcs = catalogue?.arcs || [];
    let roleCount = 0;
    const arcTotals = { received: 0, inserted: 0, unresolved: 0 };

    if (roles.length && arcs.length) {
        progress("Saving disclosure tables...");
        roleCount = await syncTaxonomyRoles(headerId, roles, config);

        const arcBatches = chunk(arcs, BATCH_SIZE);
        for (let i = 0; i < arcBatches.length; i++) {
            progress(`Saving hierarchy ${i + 1}/${arcBatches.length}...`);
            const r = await syncTaxonomyArcs(headerId, arcBatches[i], i === 0, config);
            arcTotals.received += r.received;
            arcTotals.inserted += r.inserted;
            arcTotals.unresolved += r.unresolved;
        }
    }

    return {
        headerId,
        conceptCount: Object.keys(catalogue?.concepts || {}).length,
        roleCount,
        arcCount: arcTotals.inserted,
        unresolvedArcCount: arcTotals.unresolved,
        hasHierarchy: roles.length > 0 && arcs.length > 0,
    };
};
